import { useContext, useState } from 'react';
import { ThemeContext } from '../context/ThemeContext';

const USER_GUIDE_URL = `${process.env.PUBLIC_URL}/images/Stonewall-Invoicing-User-Guide.pdf`;

const Documentation = () => {
  const { darkMode } = useContext(ThemeContext);
  const [showPdfViewer, setShowPdfViewer] = useState(false);

  const handleDownloadPdf = () => {
    const link = document.createElement('a');
    link.href = USER_GUIDE_URL;
    link.download = 'Stonewall-Invoicing-User-Guide.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className={`flex-1 flex flex-col ${darkMode ? 'dark' : ''} bg-white dark:bg-gray-900`}>
      <div className="flex-grow">

        {/* Header */}
        <div className="text-center pt-16 pb-10 px-4">
          <p className="text-sm tracking-widest uppercase text-blue-600 dark:text-blue-400 font-semibold mb-3">Documentation</p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white tracking-tight mb-4">
            Learn Stonewall Invoicing.
          </h1>
          <p className="text-lg text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
            Everything you need to get up and running.
          </p>
        </div>

        <div className="max-w-5xl mx-auto px-4 pb-20">

          {/* User Guide PDF */}
          <div className="bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md rounded-lg p-8 md:p-12 mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">User Guide</h2>
                <p className="text-gray-500 dark:text-gray-400">Complete guide to getting started (PDF)</p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowPdfViewer(!showPdfViewer)}
                  className="inline-flex items-center px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {showPdfViewer ? 'Hide' : 'View PDF'}
                </button>
                <button onClick={handleDownloadPdf}
                  className="inline-flex items-center px-5 py-2.5 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-semibold transition-colors">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            </div>

            {showPdfViewer && (
              <div className="mt-4 rounded-2xl overflow-hidden">
                <iframe
                  src={`${USER_GUIDE_URL}#toolbar=1&navpanes=1`}
                  title="Stonewall Invoicing User Guide"
                  className="w-full bg-white"
                  style={{ height: '80vh' }}
                />
              </div>
            )}
          </div>

          {/* Docs Content */}
          <div className="flex flex-col md:flex-row gap-8">

            {/* Sidebar */}
            <div className="md:w-56 flex-shrink-0">
              <div className="bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md rounded-lg p-6 sticky top-24">
                <h3 className="text-sm font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-4">Contents</h3>
                <ul className="space-y-3">
                  {['Getting Started', 'Installation', 'Basic Usage', 'Advanced Features', 'Troubleshooting', 'API Reference'].map((item) => (
                    <li key={item}>
                      <a href={`#${item.toLowerCase().replace(/\s+/g, '-')}`} className="text-blue-600 dark:text-blue-400 hover:underline text-sm">
                        {item}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Main content */}
            <div className="flex-1">
              <div className="bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md rounded-lg p-8 md:p-12">

                <section id="getting-started" className="mb-10">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Getting Started</h2>
                  <p className="text-gray-500 dark:text-gray-400 text-lg leading-relaxed mb-3">
                    Welcome to the Stonewall Invoicing documentation. This guide will help you get started with our software and make the most of its features to manage your automotive repair business efficiently.
                  </p>
                  <p className="text-gray-500 dark:text-gray-400 text-lg leading-relaxed">
                    Stonewall Invoicing is designed to streamline your workflow, from customer management to invoicing and everything in between. Whether you run a small garage or a large service center, our software will help you save time and reduce errors.
                  </p>
                </section>

                <section id="installation" className="mb-10">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Installation</h2>
                  <p className="text-gray-500 dark:text-gray-400 text-lg leading-relaxed mb-4">
                    Installing Stonewall Invoicing is straightforward. Simply download the appropriate version for your operating system and follow the on-screen instructions.
                  </p>
                  <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3">System Requirements</h4>
                    <ul className="space-y-2 text-gray-500 dark:text-gray-400">
                      {['Windows 10 or later', '4GB RAM (8GB recommended)', '1GB free disk space', '1280x768 screen resolution', 'Internet connection for updates'].map((r) => (
                        <li key={r} className="flex items-center">
                          <svg className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section id="basic-usage" className="mb-10">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Basic Usage</h2>
                  <p className="text-gray-500 dark:text-gray-400 text-lg leading-relaxed mb-3">
                    After installation, you'll need to activate your license key and complete the first-time setup. Once configured, you'll see the invoice screen — the main hub of the application.
                  </p>
                  <p className="text-gray-500 dark:text-gray-400 text-lg leading-relaxed">
                    Navigate between the three main sections using the bottom tabs: Invoice, Reports, and Settings.
                  </p>
                </section>

                <section id="advanced-features" className="mb-10">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Advanced Features</h2>
                  <p className="text-gray-500 dark:text-gray-400 text-lg leading-relaxed">
                    Stonewall Invoicing includes custom line item types with individual tax rates, flexible discounts (per-item and invoice-level), CSV data backup and restore, and automatic vehicle database updates.
                  </p>
                </section>

                <section id="troubleshooting" className="mb-10">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Troubleshooting</h2>
                  <p className="text-gray-500 dark:text-gray-400 text-lg leading-relaxed">
                    If you encounter any issues with Stonewall Invoicing, our support team is here to help. Contact us at support@stonewallinvoicing.com.
                  </p>
                </section>

                <section id="api-reference">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">API Reference</h2>
                  <p className="text-gray-500 dark:text-gray-400 text-lg leading-relaxed">
                    For developers looking to integrate with Stonewall Invoicing, we provide a comprehensive API. The API documentation covers all endpoints, authentication methods, and example requests and responses.
                  </p>
                </section>

              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Documentation;
